import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import {
  PersonSearchOutlined as PersonSearchIcon,
  HourglassBottomOutlined as HourglassBottomIcon,
  LayersOutlined as LayersIcon,
  VisibilityOutlined as VisibilityIcon,
  SettingsOutlined as SettingsIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  PermMedia as PermMediaIcon,
  Subscriptions as SubscriptionsIcon,
  ExpandMore as ExpandMoreIcon,
  MonitorHeartOutlined as MonitorIcon,
  ShoppingCartOutlined as CartIcon,
  ConnectedTvOutlined as CtvIcon,
  ManageSearchOutlined,
  ReceiptLongOutlined,
  Group as GroupIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { AppContext } from "../../../pages/root/Root";
import { initLocalization } from "../../../utils/helpers";
import {
  ROUTE_ADMIN,
  ROUTE_LOGIN,
  ROUTE_MAIN,
  ROUTE_PATHMATICS_LEVEL2,
  ROUTE_PATHMATICS_LEVEL3,
  ROUTE_BIS_LEVEL1,
  ROUTE_BIS_LEVEL2,
  ROUTE_BIS_LEVEL3,
  ROUTE_BIS_MONITORING,
  ROUTE_MAPPINGS_VIEW,
  ROUTE_ALL_MAPPINGS_MEDIA,
  ROUTE_PROVIDER_WORKFLOW_MONITORING,
  ROUTE_DATA_INGESTION,
  ROUTE_MEDIA_CLASSIFICATION,
  ROUTE_E_RETAIL_LEVEL1,
  ROUTE_E_RETAIL_LEVEL2,
  ROUTE_E_RETAIL_LEVEL3,
  ROUTE_E_RETAIL_LEVEL3_DT,
  ROUTE_E_RETAIL_SPENDS,
  ROUTE_CTV_LEVEL2,
  ROUTE_CTV_LEVEL3,
  API_ROUTE_LOG_OUT,
  API_ROUTE_MAPPING_UNLOCK_ALL_LINES,
  API_ROUTE_E_RETAIL_BUYERS_LOCKED_LINES,
} from "../../../routes";
import { MENU_SECTIONS } from "../../../constants";
import Api from "../../../utils/Api";
import { burgerMenuStrings } from "./locale";
import "./style.scss";

export const BurgerMenu = ({ open, onClose }) => {
  initLocalization(burgerMenuStrings);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useContext(AppContext);
  const [expanded, setExpanded] = useState("");

  const pathname = location.pathname.split("/")?.[1];

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleLogout = async () => {
    await Api.patch(API_ROUTE_MAPPING_UNLOCK_ALL_LINES);
    await Api.patch(API_ROUTE_E_RETAIL_BUYERS_LOCKED_LINES, {
      shouldLock: false,
    });
    await Api.post(API_ROUTE_LOG_OUT);
    navigate(ROUTE_LOGIN, { replace: true });
  };

  return (
    <Drawer
      className="burger-menu"
      open={open}
      elevation={3}
      hideBackdrop={true}
      onClose={onClose}
      data-testid="burger-menu-list"
      disableEnforceFocus
    >
      <List
        subheader={
          <ListSubheader className="burger-menu__header" sx={{ p: 1 }}>
            <IconButton
              onClick={onClose}
              aria-label={burgerMenuStrings.closeMenu}
              data-testid="close-burger-menu"
            >
              <CloseIcon color="primary" sx={{ width: 20, height: 20 }} />
            </IconButton>
          </ListSubheader>
        }
      >
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.pathmatics}
          onChange={handleChange(MENU_SECTIONS.pathmatics)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.pathmatics || pathname === ""
                ? "selected"
                : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.pathmatics}-content`}
            id={`${MENU_SECTIONS.pathmatics}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} pt={1}>
                <ListItemIcon>
                  <PersonSearchIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.paidSocialCategory}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>
          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_MAIN}
                data-testid="pathmatics-level1-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.mappingLevel}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_PATHMATICS_LEVEL2}
                data-testid="pathmatics-level2-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.creativeClassification}
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_PATHMATICS_LEVEL3}
                data-testid="pathmatics-level3-link"
              >
                <ListItemIcon>
                  <VisibilityIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.creativeReview}</ListItemText>
              </ListItemButton>
            </ListItem>
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />
      <List>
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.bis}
          onChange={handleChange(MENU_SECTIONS.bis)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.bis ? "selected" : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.bis}-content`}
            id={`${MENU_SECTIONS.bis}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} paddingTop={1}>
                <ListItemIcon>
                  <HourglassBottomIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.displayCategory}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>
          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_BIS_LEVEL1}
                data-testid="bis-level1-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.mappingLevel}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_BIS_LEVEL2}
                data-testid="bis-level2-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.creativeClassification}
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_BIS_LEVEL3}
                data-testid="bis-level3-link"
              >
                <ListItemIcon>
                  <VisibilityIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.creativeReview}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_BIS_MONITORING}
                data-testid="bis-monitoring-link"
              >
                <ListItemIcon>
                  <MonitorIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.monitoring}</ListItemText>
              </ListItemButton>
            </ListItem>
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />
      <List>
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.selfPromotion}
          onChange={handleChange(MENU_SECTIONS.selfPromotion)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.selfPromotion ? "selected" : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.selfPromotion}-content`}
            id={`${MENU_SECTIONS.selfPromotion}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} paddingTop={1}>
                <ListItemIcon>
                  <PermMediaIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.selfPromotionCategory}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>
          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_MAPPINGS_VIEW}
                data-testid="self-promotion-mapping-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.mappingsView}</ListItemText>
              </ListItemButton>
            </ListItem>
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />
      <List>
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.allMapping}
          onChange={handleChange(MENU_SECTIONS.allMapping)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.allMapping ? "selected" : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.allMapping}-content`}
            id={`${MENU_SECTIONS.allMapping}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} paddingTop={1}>
                <ListItemIcon>
                  <SubscriptionsIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.mediaCategory}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>
          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_ALL_MAPPINGS_MEDIA}
                data-testid="media-all-mappings-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.media}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_PROVIDER_WORKFLOW_MONITORING}
                data-testid="workflow-monitoring-link"
              >
                <ListItemIcon>
                  <MonitorIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.providerworkflowMonitoring}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />

      <List>
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.audience}
          onChange={handleChange(MENU_SECTIONS.audience)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.audience ? "selected" : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.audience}-content`}
            id={`${MENU_SECTIONS.audience}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} paddingTop={1}>
                <ListItemIcon>
                  <GroupIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.audience}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>

          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_DATA_INGESTION}
                data-testid="audience-data-ingestion-link"
              >
                <ListItemIcon>
                  <CloudUploadIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.dataIngestion}</ListItemText>
              </ListItemButton>
            </ListItem>

            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_MEDIA_CLASSIFICATION}
                data-testid="audience-media-classification-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.mediaClassification}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />

      <List>
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.eRetail}
          onChange={handleChange(MENU_SECTIONS.eRetail)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.eRetail ? "selected" : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.eRetail}-content`}
            id={`${MENU_SECTIONS.eRetail}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} paddingTop={1}>
                <ListItemIcon>
                  <CartIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.eRetail}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>
          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_E_RETAIL_LEVEL1}
                data-testid="eretail-level1-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.dataAssignment}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_E_RETAIL_LEVEL2}
                data-testid="eretail-level2-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.creativeClassification}
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_E_RETAIL_LEVEL3}
                data-testid="eretail-level3-link"
              >
                <ListItemIcon>
                  <VisibilityIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.creativeReview}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_E_RETAIL_LEVEL3_DT}
                data-testid="eretail-level3-DT-link"
              >
                <ListItemIcon>
                  <ManageSearchOutlined color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.creativeAnalysis}
                </ListItemText>
              </ListItemButton>
            </ListItem>
            {/* <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_E_RETAIL_SPENDS}
                data-testid="eretail-spends-link"
              >
                <ListItemIcon>
                  <ReceiptLongOutlined color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.spends}</ListItemText>
              </ListItemButton>
            </ListItem> */}
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />

      <List>
        <Accordion
          className="burger-menu__section"
          expanded={expanded === MENU_SECTIONS.ctv}
          onChange={handleChange(MENU_SECTIONS.ctv)}
        >
          <AccordionSummary
            className={`burger-menu__title ${
              pathname === MENU_SECTIONS.ctv ? "selected" : ""
            }`}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${MENU_SECTIONS.ctv}-content`}
            id={`${MENU_SECTIONS.ctv}-header`}
          >
            <ListItem>
              <Grid display="flex" flexDirection="row" gap={0.5} paddingTop={1}>
                <ListItemIcon>
                  <CtvIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& p": {
                      fontWeight: "bold",
                      fontSize: "1rem",
                    },
                    mt: 0,
                  }}
                >
                  {burgerMenuStrings.connectedTv}
                </ListItemText>
              </Grid>
            </ListItem>
          </AccordionSummary>
          <AccordionDetails className="burger-menu__element">
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_CTV_LEVEL2}
                data-testid="ctv-level2-link"
              >
                <ListItemIcon>
                  <LayersIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>
                  {burgerMenuStrings.creativeClassification}
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTE_CTV_LEVEL3}
                data-testid="ctv-level3-link"
              >
                <ListItemIcon>
                  <VisibilityIcon color="secondary" />
                </ListItemIcon>
                <ListItemText>{burgerMenuStrings.creativeReview}</ListItemText>
              </ListItemButton>
            </ListItem>
          </AccordionDetails>
        </Accordion>
      </List>
      <Divider />
      <List>
        {isAdmin && (
          <ListItem>
            <ListItemButton
              component={NavLink}
              to={ROUTE_ADMIN}
              data-testid="admin-page-link"
            >
              <ListItemIcon>
                <SettingsIcon color="secondary" />
              </ListItemIcon>
              <ListItemText>{burgerMenuStrings.admin}</ListItemText>
            </ListItemButton>
          </ListItem>
        )}
        <ListItem>
          <ListItemButton onClick={handleLogout} data-testid="log-out-link">
            <ListItemIcon>
              <LogoutIcon color="secondary" />
            </ListItemIcon>
            <ListItemText>{burgerMenuStrings.logout}</ListItemText>
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

BurgerMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
